<template>
  <div
    v-if="Object.keys(buttonRule).length"
    class="group flex h-18 cursor-pointer transition-[flex-grow] duration-300"
    :class="active ? 'min-w-0 basis-1/2 grow' : 'w-9 min-w-9 grow-0 hover:grow-20'"
  >
    <SimpleButton
      class="h-full w-full px-2!"
      :class="side === 'left' ? 'rounded-r-none!' : 'rounded-l-none!'"
      @click="$emit('click')"
    >
      <div
        class="absolute inset-y-0 right-0 z-0 bg-text/15 transition-[width] duration-300"
        :style="{ width: `${autoClickTimerProgress}%` }"
      ></div>

      <div
        class="relative z-1 flex h-full w-full items-center justify-center gap-1.5"
        :class="side === 'left' ? 'flex-row' : 'flex-row-reverse'"
      >
        <div class="flex size-4 shrink-0 items-center justify-center">
          <span v-if="buttonRule[EVENT__UNASSIGN]" class="tr tr-question-mark"></span>
          <IconChevronLeft16Solid v-else-if="moveStageName && side === 'left'" class="size-4" />
          <IconChevronRight16Solid v-else-if="moveStageName" class="size-4" />
          <UserAvatar
            v-else-if="buttonRule[EVENT__ASSIGN]"
            class="rule-icon-avatar size-4!"
            :user="{ ...getUserWithCriteria(buttonRule[EVENT__ASSIGN]), registrationCompleted: true }"
          />
          <UserAvatar
            v-else-if="buttonRule[EVENT__ASSIGN_IF_NOTASSIGNED] && !assignedMember"
            class="rule-icon-avatar size-4!"
            :user="{
              ...getUserWithCriteria(buttonRule[EVENT__ASSIGN_IF_NOTASSIGNED]),
              registrationCompleted: true,
            }"
          />
          <UserAvatar
            v-else-if="buttonRule[EVENT__ASSIGN_MENTION]"
            class="rule-icon-avatar size-4!"
            :user="{
              ...(transferUser ? getMentionUser() : { username: '@' }),
              registrationCompleted: true,
            }"
          />
        </div>

        <div
          class="min-w-0 flex-col items-center gap-1 text-label whitespace-nowrap"
          :class="active ? 'flex' : 'hidden group-hover:flex'"
        >
          <div v-if="moveStageName" class="flex max-w-full items-center gap-1">
            {{ $t('task.move_to') }}
            <span class="min-w-0 truncate font-bold">{{ moveStageName }}</span>
          </div>
          <div v-if="buttonRule[EVENT__ASSIGN]" class="flex items-center gap-1">
            {{ $t('task.assign') }}
            <UserAvatar
              class="rule-info-avatar size-5!"
              :user="{ ...getUserWithCriteria(buttonRule[EVENT__ASSIGN]), registrationCompleted: true }"
            />
          </div>
          <div
            v-if="buttonRule[EVENT__ASSIGN_IF_NOTASSIGNED] && !assignedMember"
            class="flex items-center gap-1"
          >
            {{ $t('task.assign') }}
            <UserAvatar
              class="rule-info-avatar size-5!"
              :user="{
                ...getUserWithCriteria(buttonRule[EVENT__ASSIGN_IF_NOTASSIGNED]),
                registrationCompleted: true,
              }"
            />
          </div>
          <div v-if="buttonRule[EVENT__ASSIGN_MENTION]" class="flex items-center gap-1">
            {{ $t('task.assign') }}
            <UserAvatar
              class="rule-info-avatar size-5!"
              :user="{
                ...(transferUser ? getMentionUser() : { username: '@' }),
                registrationCompleted: true,
              }"
            />
          </div>
          <div v-if="buttonRule[EVENT__UNASSIGN]" class="flex items-center gap-1">
            {{ $t('task.unassign') }}
            <span class="tr tr-question-mark"></span>
          </div>
        </div>
      </div>
    </SimpleButton>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Lodash from 'lodash';

import {
  EVENT__UNASSIGN, // Unassign
  EVENT__ASSIGN, // ASSIGN MEMBER
  EVENT__ASSIGN_IF_NOTASSIGNED, // Assign if not assigned
  EVENT__ASSIGN_MENTION, // Assign if not assigned
  EVENT__AUTOCLICK, // AUTO CLICK AFTER ** seconds
  EVENT__MOVE, // Move to <Stage>
  EXISTING__GROUPS,
} from '@/utils/constantHolder';
import SimpleButton from './SimpleButton.vue';
import {
  IconChevronLeft16Solid,
  IconChevronRight16Solid,
} from '@iconify-prerendered/vue-heroicons';

const props = defineProps({
  active: Boolean,
  rule: Array,
  stages: Array,
  transferUser: null,
  assignedMember: null,
  taskCreator: null,
  showMe: null,
  side: String,
  currentStageId: String,
  shouldAutoClick: Boolean,
  autoclickStartPoint: Number,
});
// declared, so the listener does not also fall through to the root element and fire the rule twice
defineEmits(['click']);

const { t } = useI18n();
const store = useStore();

const user = computed(() => store.state.user.user);
const getUser = (id) => store.getters['user/getUser'](id);

const timeLeft = ref(0);
let countDownInterval = null;

const buttonRule = computed(() => {
  const filterRules = EXISTING__GROUPS.reduce((acc, itemGroup) => {
    const action = Lodash.findLast(props.rule,
      rule => itemGroup.find(item => item === rule.action));
    if (!action) {
      return { ...acc };
    }
    const nameAction = action.action;
    const value = action[Object.keys(action)[1]]?.toString() ?? 'none';

    return { ...acc, [nameAction]: value };
  }, {});
  return filterRules;
});

const moveStageName = computed(() => {
  if (!buttonRule.value[EVENT__MOVE]) {
    return false;
  }
  let stage = props.stages?.find(x => x.id === buttonRule.value.MOVE);

  if (!stage) {
    const stageIdx = props.stages?.findIndex(x => x.id === props.currentStageId);

    if (stageIdx === -1 || stageIdx === undefined) {
      return false;
    }
    if (buttonRule.value.MOVE === 'prev_stage') {
      stage = props.stages[stageIdx - 1];
    } else if (buttonRule.value.MOVE === 'next_stage') {
      stage = props.stages[stageIdx + 1];
    }
  }
  if (!stage) {
    return false;
  }
  return stage.name;
});

const autoClickAfter = computed(() => {
  const autoClickAction = props.rule.find(r => r.action === EVENT__AUTOCLICK);
  return props.shouldAutoClick && autoClickAction?.duration * 60;
});

const autoClickTimerProgress = computed(() => (
  autoClickAfter.value ? (timeLeft.value * 100) / autoClickAfter.value : 0
));

const getUserWithCriteria = (userId) => {
  if (userId === 'creator') {
    if (props.taskCreator) {
      return getUser(props.taskCreator);
    }
    return { username: t('projectRules.Task_creator') };
  }
  if (userId === '_me_') {
    if (props.showMe) {
      if (!user.value) {
        return false;
      }
      if (!user.value) {
        return false;
      }
      return getUser(user.value.id);
    }
    return { username: t('projectRules.Me') };
  }
  return getUser(userId);
};

const getMentionUser = () => {
  if (props.transferUser) {
    return getUser(props.transferUser);
  }
  return undefined;
};

watch(() => props.shouldAutoClick, (value) => {
  if (value && autoClickAfter.value) {
    timeLeft.value = autoClickAfter.value - (props.autoclickStartPoint || 0);
    const steps = 1000.0;
    const intervalSeconds = autoClickAfter.value / steps;
    countDownInterval = setInterval(() => {
      timeLeft.value -= intervalSeconds;
      if (timeLeft.value < 0) {
        clearInterval(countDownInterval);
      }
    }, intervalSeconds * 1000);
  }
});
</script>
<style lang="scss" scoped>
// the initials must scale down with the button-sized avatars
.rule-info-avatar :deep(.content) {
  font-size: var(--fs-unit);
}

.rule-icon-avatar :deep(.content) {
  font-size: 0.5rem;
}
</style>
